import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorAlert, Field, InfoField, MfaSetupAlert } from "src/components";
import { VerificationType } from "src/api";
import PaymentLimits from "src/components/paymentLimits/PaymentLimits";
import DropdownAnimation from "src/components/UI/dropdownAnimation/DropdownAnimation";
import { useAlfredWithdrawal } from "src/hooks";
import { alfredLimitsHint, formatNumberToLocale } from "src/utils";
import AlfredAddBankAccountForm from "./AlfredAddBankAccountForm";

// English defaults for the PIX key-kind prefix shown in saved-account
// labels; pt/ar resolve via the ALFRED.PIX_KEY_TYPE.* i18n keys.
const PIX_KEY_TYPE_LABEL: Record<string, string> = {
  CPF: "CPF",
  EMAIL: "Email",
  PHONE: "Phone",
  EVP: "Random key",
};

/**
 * Single-screen Alfred withdrawal form. Mirrors `DepositForm` in
 * structure: target-fiat picker → amount → payment-method picker →
 * fees breakdown → submit. Once the offramp is created, swaps the
 * form for {@link AlfredWithdrawalInstructions} (status panel).
 */
const AlfredWithdrawalForm: React.FC<{ verificationType?: VerificationType }> = ({ verificationType }): JSX.Element => {
  const { t } = useTranslation();
  const w = useAlfredWithdrawal(verificationType);
  const { currency_id, precision } = w.currency;
  // Cap stable-side display precision at 2 to match fiat formatting —
  // platform sets USDT precision to 3 which renders "199.590 USDT".
  const displayPrecision = Math.min(precision, 2);

  const alfredFeeTotal = useMemo(() => {
    if (!w.quote) return 0;
    return w.quote.fees.reduce((sum, f) => sum + Number(f.amount), 0);
  }, [w.quote]);

  // Net fiat the user actually receives after Alfred's deduction
  // (already baked into `quote.toAmount`). Platform fee is taken on
  // the USDC side, so we show it next to the amount the user spends.
  const fiatReceiveNet = w.fiatReceiveGross;

  // Keep the Add-bank popup mounted through its exit animation: the open
  // state (`w.addBankOpen`) flips instantly, so without this the dialog
  // would just vanish on close. `bankMounted` holds it in the DOM and
  // `bankClosing` swaps to the reverse keyframes for the close duration.
  const ADD_BANK_ANIM_MS = 220;
  const [bankMounted, setBankMounted] = useState(false);
  const [bankClosing, setBankClosing] = useState(false);
  useEffect(() => {
    if (w.addBankOpen) {
      setBankMounted(true);
      setBankClosing(false);
      return;
    }
    if (!bankMounted) return;
    setBankClosing(true);
    const timer = setTimeout(() => {
      setBankMounted(false);
      setBankClosing(false);
    }, ADD_BANK_ANIM_MS);
    return () => clearTimeout(timer);
  }, [w.addBankOpen, bankMounted]);

  const dropdownOptions = useMemo(
    () =>
      w.options.map((opt) => ({
        key: opt.source_currency,
        value: `${opt.source_currency} — ${opt.rail}`,
      })),
    [w.options]
  );

  // FiatAccount labels: prefer the rail-specific accountAlias / accountName
  // when present, then fall back to a "type — ****last4" pattern derived
  // from accountNumber. Status filtering is intentionally absent — Alfred's
  // fiatAccount controller doesn't carry the same `pending/active`
  // gating as the legacy payment-methods one; if Alfred lists the
  // account, the widget shows it.
  const paymentMethodOptions = useMemo(
    () =>
      w.fiatAccounts.map((fa) => {
        // Alfred's GET /fiatAccounts puts identifiers at the TOP level
        // (accountNumber, accountName, bankName), not nested under
        // `fiatAccountFields` the way create payloads do.
        type FlatFiat = {
          accountName?: string;
          accountAlias?: string;
          accountNumber?: string;
          accountType?: string;
          bankName?: string;
        };
        const flat = fa as FlatFiat;
        const nested = (fa.fiatAccountFields ?? {}) as FlatFiat;
        const alias = flat.accountAlias ?? nested.accountAlias;
        const holder = flat.accountName ?? nested.accountName;
        const bank = flat.bankName ?? nested.bankName;
        const accountNumber = flat.accountNumber ?? nested.accountNumber;
        const accountType = flat.accountType ?? nested.accountType;

        // PIX keys live in `accountNumber`, their kind in `accountType`
        // (CPF / EMAIL / PHONE / EVP). A blind slice(-4) collapses every
        // gmail key to "****.com" — indistinguishable — so label by kind:
        // emails keep head + domain; digit/opaque keys keep the last 4.
        // The kind prefix also tells a CPF apart from a random (EVP) key.
        if (fa.type === "PIX" && accountType) {
          const kind = accountType.toUpperCase();
          const kindName = t(`ALFRED.PIX_KEY_TYPE.${kind}`, {
            defaultValue: PIX_KEY_TYPE_LABEL[kind] ?? kind,
          });
          const key = (accountNumber ?? "").trim();
          let masked: string;
          if (kind === "EMAIL" && key.includes("@")) {
            const [local, domain] = key.split("@");
            masked = `${local.slice(0, 2) || local}***@${domain}`;
          } else {
            masked = key ? `****${key.slice(-4)}` : "";
          }
          return {
            key: fa.fiatAccountId,
            value: masked ? `${kindName} · ${masked}` : kindName,
          };
        }

        const last4 = accountNumber?.slice(-4);

        // ALWAYS append the last-4 suffix when available — Alfred
        // often returns `accountName == bankName` ("BANAMEX" /
        // "BANAMEX") so without the digits two accounts at the same
        // bank are indistinguishable. Display priority for the
        // primary label: user-set alias → holder name (when distinct
        // from bank) → bank → rail.
        const primary =
          alias ??
          (holder && holder !== bank ? holder : null) ??
          bank ??
          fa.type;
        // Alfred returns very long resolved bank names ("BANCO POPULAR
        // DOMINICANO BANCO MULTIPLE, S.A.") that wrap the dropdown into
        // 3 lines. Truncate the primary to keep the ****last4 suffix
        // (the actual disambiguator between two accounts at the same
        // bank) on the same row.
        const MAX_PRIMARY_LEN = 24;
        const truncated =
          primary && primary.length > MAX_PRIMARY_LEN
            ? `${primary.slice(0, MAX_PRIMARY_LEN - 1).trimEnd()}…`
            : primary;
        const label = last4 ? `${truncated} — ****${last4}` : truncated;
        return {
          key: fa.fiatAccountId,
          value: label,
        };
      }),
    [w.fiatAccounts, t]
  );

  const hint = w.selectedOption ? alfredLimitsHint(w.selectedOption, currency_id) : null;
  const limitsHint = hint ? t(hint.key, hint.options) : undefined;

  const firstError =
    w.errors.balanceErrors[0] ??
    w.errors.inputErrors[0] ??
    w.errors.paymentValidationErrors[0] ??
    w.errors.paymentMethodErrors[0] ??
    w.errors.paymentFeeErrors[0];

  // Withdrawal happy-path UX: we don't poll Alfred for status after
  // submit — US ACH alone can take days to settle and live stepper
  // would either lie or sit empty. Show a confirmation card with the
  // submitted amount + quote so the user gets clear feedback and
  // closes; they're notified by email/push on final completion.
  if (w.submittedSnapshot) {
    return (
      <section className="deposit-form deposit-form--success">
        <div className="deposit-form__success-icon" aria-hidden>
          ✓
        </div>
        <h2 className="deposit-form__success-title">
          {t("WITHDRAWAL_FORM.SUBMITTED_TITLE")}
        </h2>
        <p className="deposit-form__success-text">
          {t("WITHDRAWAL_FORM.SUBMITTED_TEXT")}
        </p>
        <InfoField
          label={t("WITHDRAWAL_FORM.YOU_SEND")}
          value={`${w.submittedSnapshot.fromAmount} ${w.submittedSnapshot.fromCurrency}`}
        />
        <InfoField
          label={t("WITHDRAWAL_FORM.YOU_RECEIVE")}
          value={
            w.submittedSnapshot.toAmount
              ? `${formatNumberToLocale(Number(w.submittedSnapshot.toAmount), 2)} ${w.submittedSnapshot.toCurrency}`
              : "—"
          }
        />
        <Button
          title={t("WITHDRAWAL_FORM.DONE")}
          className="deposit-button"
          type="button"
          onClick={w.resetWithdrawal}
        />
      </section>
    );
  }

  if (!w.optionsLoading && w.options.length === 0) {
    return (
      <section className="deposit-form deposit-form--unavailable">
        <div className="deposit-form__unavailable-icon" aria-hidden>
          ✕
        </div>
        <h2 className="deposit-form__unavailable-title">{t("WITHDRAWAL_FORM.METHOD_NOT_AVAILABLE_TITLE")}</h2>
        <p className="deposit-form__unavailable-text">{t("WITHDRAWAL_FORM.METHOD_NOT_AVAILABLE_TEXT")}</p>
      </section>
    );
  }

  return (
    <form className="deposit-form" onSubmit={w.handleInitiateWithdrawal}>
      {/* Same rule as DepositForm's "Pay with": hide the picker when only
          one fiat × rail is configured — today's typical setup. Reappears
          automatically if Alfred starts returning multiple options. */}
      {dropdownOptions.length > 1 && (
        <DropdownAnimation
          label={t("WITHDRAWAL_FORM.RECEIVE_IN")}
          selectedValue={w.selectedCurrency}
          options={dropdownOptions}
          placeholder={t("DEPOSIT_FORM.SELECT_CURRENCY")}
          onChange={(field) => w.setSelectedCurrency(field.key)}
          disabled={w.optionsLoading || w.submitLoading}
        />
      )}

      <Field
        label={t("WITHDRAWAL_FORM.AMOUNT_IN_CURRENCY", { currency: currency_id })}
        value={w.amount}
        onChange={w.handleAmountChange}
        placeholder={limitsHint ?? t("DEPOSIT_FORM.AMOUNT_PLACEHOLDER_ZERO")}
        disabled={w.submitLoading || !w.selectedOption}
        error={w.amountTouched ? firstError : undefined}
        onBlur={w.handleAmountBlur}
      />

      {/* Dropdown + inline `+` icon to open the Add-bank-account form.
          The `+` lives outside DropdownAnimation (the third-party
          component owns its trailing chevron) so we render them in a
          flex row instead of trying to inject the icon into the input.
          Icon visibility is gated on a session existing — we need
          customerId resolved before we can POST a new fiatAccount. */}
      <div className="alfred-payment-method-row">
        <DropdownAnimation
          wrapperClass="alfred-payment-method-row__dropdown"
          label={t("WITHDRAWAL_FORM.PAYMENT_METHOD")}
          selectedValue={w.selectedFiatAccountId}
          options={paymentMethodOptions}
          placeholder={
            w.fiatAccountsLoading
              ? t("WITHDRAWAL_FORM.LOADING_METHODS")
              : paymentMethodOptions.length === 0
                ? t("WITHDRAWAL_FORM.NO_PAYMENT_METHODS")
                : t("WITHDRAWAL_FORM.SELECT_PAYMENT_METHOD")
          }
          onChange={(field) => w.setSelectedFiatAccountId(field.key)}
          disabled={w.fiatAccountsLoading || w.submitLoading || paymentMethodOptions.length === 0}
          hideArrow={paymentMethodOptions.length === 0}
        />

        {w.sessionId && w.selectedOption && !w.addBankOpen && (
          <button
            type="button"
            className="alfred-payment-method-row__add"
            onClick={() => w.setAddBankOpen(true)}
            disabled={w.submitLoading}
            aria-label="Add bank account"
            title="Add bank account"
          >
            +
          </button>
        )}
      </div>

      {/* Add-bank form is a full-screen sub-panel: it fills the whole
          widget viewport (no dim backdrop) so the fields never feel
          cramped and there's no layout jump on the form behind. Cancel
          closes it. */}
      {w.sessionId && w.selectedOption && bankMounted && (
        <div
          className={`alfred-bank-overlay__dialog${bankClosing ? " alfred-bank-overlay__dialog--closing" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Add bank account"
        >
          <AlfredAddBankAccountForm
            sourceCurrency={w.selectedOption.source_currency}
            submitting={w.addBankLoading}
            error={w.addBankError}
            onSubmit={w.handleAddFiatAccount}
            onCancel={() => w.setAddBankOpen(false)}
          />
        </div>
      )}

      <InfoField
        className="info-field--muted"
        label={t("DEPOSIT_FORM.PLATFORM_FEE")}
        value={`${formatNumberToLocale(w.paymentFee, displayPrecision)} ${currency_id}`}
        isLoading={w.paymentFeeLoading}
      />

      <InfoField
        className="info-field--muted"
        label={
          w.selectedOption ? t("DEPOSIT_FORM.ALFRED_FEES_VIA", { rail: w.selectedOption.rail }) : t("DEPOSIT_FORM.ALFRED_FEES")
        }
        value={w.quote ? `${formatNumberToLocale(alfredFeeTotal, 2)} ${w.quote.toCurrency}` : "—"}
        isLoading={w.quoteLoading}
      />

      <InfoField
        label={t("WITHDRAWAL_FORM.RECEIVE_TOTAL")}
        value={
          w.quote && w.selectedOption ? `${formatNumberToLocale(fiatReceiveNet, 2)} ${w.selectedOption.source_currency}` : "—"
        }
        isLoading={w.quoteLoading}
      />

      <PaymentLimits
        limitsStatus={w.limitsStatus}
        currency={currency_id}
        precision={displayPrecision}
        isLoading={w.limitsLoading}
      />

      {w.mfaRequired && (
        <Field
          label={t("WITHDRAWAL_STEPS.MFA_CODE")}
          value={w.mfaToken}
          onChange={w.handleMfaTokenChange}
          placeholder={t("WITHDRAWAL_STEPS.ENTER_YOUR_MFA_CODE")}
          disabled={w.submitLoading}
          error={w.mfaError ?? undefined}
          maxLength={6}
        />
      )}

      {w.passkeyRequired && <MfaSetupAlert message={t("WITHDRAWAL_FORM.PASSKEY_REQUIRED")} />}
      {w.verificationSetupRequired && <MfaSetupAlert message={t("MFA.SETUP_VERIFICATION_TO_PROCEED_WITH_WITHDRAWALS")} />}

      <ErrorAlert message={w.submitError} />

      <Button
        title={w.passkeyRequired ? t("WITHDRAWAL_FORM.CONFIRM_WITH_PASSKEY") : t("WITHDRAWAL_FORM.WITHDRAW")}
        className="deposit-button"
        type="submit"
        disabled={w.submitDisabled}
        pending={w.submitLoading}
        tooltipMessage={Object.values(w.errors).flat()[0]}
      />
    </form>
  );
};

export default AlfredWithdrawalForm;
